if (!customElements.get("hero-carousel")) {
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();

      this.slides = this.querySelectorAll(".hero-carousel__slide");
      this.track = this.querySelector(".hero-carousel__track");
      if (this.slides.length < 2) {
        return;
      }
      this.init();
    }

    init() {
      this.check = setInterval(() => {
        const track = this.querySelector("flickity-component");
        this.sliderInstance = track && track.slider && track.slider.instance;
        if (this.sliderInstance && typeof this.sliderInstance === "object") {
          clearInterval(this.check);
          this.sliderInstance.on("change", this.handleChange.bind(this));
          this.updateFocusableSlides(this.sliderInstance.selectedIndex || 0);
          this.initAutoplayToggle();

          // Flickity stops autoplay for good the first time the user
          // drags, uses the arrows/dots, or navigates by keyboard. Once
          // that happens every future slide change is user-driven, so
          // announce it instead of leaving aria-live silenced.
          if (this.track && this.track.getAttribute("aria-live") === "off") {
            this.sliderInstance.on("uiChange", () => {
              this.track.setAttribute("aria-live", "polite");
            });
          }
        }
      }, 100);
    }

    disconnectedCallback() {
      clearInterval(this.check);
      if (this.autoplayToggle && this.handleAutoplayToggle) {
        this.autoplayToggle.removeEventListener("click", this.handleAutoplayToggle);
      }
    }

    initAutoplayToggle() {
      this.autoplayToggle = this.querySelector(".hero-carousel__autoplay");
      if (!this.autoplayToggle) return;

      this.handleAutoplayToggle = () => {
        const paused = this.autoplayToggle.classList.toggle("is-paused");
        const { pauseLabel, playLabel } = this.autoplayToggle.dataset;

        if (paused) {
          this.sliderInstance.stopPlayer();
          this.autoplayToggle.setAttribute("aria-label", playLabel);
          // Rotation is now user-driven, so announcing a slide change is
          // useful rather than an interruption. Matches the uiChange rule above.
          this.track && this.track.setAttribute("aria-live", "polite");
        } else {
          this.sliderInstance.playPlayer();
          this.autoplayToggle.setAttribute("aria-label", pauseLabel);
          this.track && this.track.setAttribute("aria-live", "off");
        }
      };

      this.autoplayToggle.addEventListener("click", this.handleAutoplayToggle);
    }

    handleChange(index) {
      // Exclude the CTAs: resetAnimation/refreshAnimation replay the
      // fade-in-up transform (translateY 2rem -> 0) on every slide change,
      // including revisits. A click landing during that ~0.5s settle
      // window hits the button's pre-final position and misses - the
      // buttons need to stay put once their one-time entrance animation
      // (triggered separately, on first reveal) has played.
      this.slides.forEach((slide, i) => {
        const motionEls = slide.querySelectorAll(
          "motion-element:not(.hero-carousel__ctas)"
        );
        if (i === index) {
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              typeof motionEl.refreshAnimation === "function" &&
                motionEl.refreshAnimation();
            });
          });
        } else {
          motionEls.forEach((motionEl) => {
            typeof motionEl.resetAnimation === "function" &&
              motionEl.resetAnimation();
          });
        }
      });
      this.updateFocusableSlides(index);
    }

    // Flickity marks off-screen slides aria-hidden, but leaves their links
    // and buttons focusable - a keyboard user can tab into controls they
    // can't see. Strip inactive slides out of tab order and restore
    // whatever tabindex (if any) the active slide's elements had.
    updateFocusableSlides(activeIndex) {
      this.slides.forEach((slide, i) => {
        const focusable = slide.querySelectorAll(
          "a[href], button, input, select, textarea, [tabindex]"
        );
        focusable.forEach((el) => {
          if (i === activeIndex) {
            if (el.hasAttribute("data-hero-carousel-tabindex")) {
              const original = el.getAttribute("data-hero-carousel-tabindex");
              if (original) {
                el.setAttribute("tabindex", original);
              } else {
                el.removeAttribute("tabindex");
              }
              el.removeAttribute("data-hero-carousel-tabindex");
            }
          } else if (!el.hasAttribute("data-hero-carousel-tabindex")) {
            el.setAttribute(
              "data-hero-carousel-tabindex",
              el.getAttribute("tabindex") || ""
            );
            el.setAttribute("tabindex", "-1");
          }
        });
      });
    }
  }

  customElements.define("hero-carousel", HeroCarousel);
}
